import React from 'react';
import { cn } from '../../lib/utils';
import { usePageContext, generateAutoTrackingId, pushGTMEvent } from '../../utils/tracking';

interface TabsContextType {
  value: string;
  onValueChange: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextType | undefined>(undefined);

interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

const Tabs = ({ value, onValueChange, children, className }: TabsProps) => {
  return (
    <TabsContext.Provider value={{ value, onValueChange }}>
      <div className={cn('w-full', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

const TabsList = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'inline-flex h-9 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-700 p-1 text-gray-500 dark:text-gray-400',
      className
    )}
    {...props}
  />
));
TabsList.displayName = 'TabsList';

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  // Tracking props
  trackingId?: string;
  trackingSection?: string;
  disableTracking?: boolean;
}

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ 
    className, 
    value, 
    children, 
    onClick,
    trackingId,
    trackingSection,
    disableTracking = false,
    ...props 
  }, ref) => {
    const context = React.useContext(TabsContext);
    const { pageName, section } = usePageContext();
    
    if (!context) {
      throw new Error('TabsTrigger must be used within a Tabs component');
    }

    const { value: selectedValue, onValueChange } = context;
    const isActive = selectedValue === value;

    // Generate tracking ID if not provided and tracking is enabled
    const autoTrackingId = !disableTracking && !trackingId ? 
      generateAutoTrackingId({
        elementType: 'tab',
        pageName,
        children,
        section: trackingSection || section,
        variant: isActive ? 'active' : 'inactive'
      }) : trackingId;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!disableTracking && autoTrackingId) {
        pushGTMEvent({
          elementType: 'tab',
          elementId: autoTrackingId,
          elementText: typeof children === 'string' ? children : value,
          pageName,
          section: trackingSection || section,
          variant: isActive ? 'active' : 'inactive',
          userAction: 'click'
        });
      }
      
      onValueChange(value);
      
      if (onClick) {
        onClick(event);
      }
    };

    return (
      <button
        ref={ref}
        className={cn(
          'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1 text-sm font-medium ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-gray-800 dark:focus-visible:ring-blue-400',
          isActive
            ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
          className
        )}
        onClick={handleClick}
        id={autoTrackingId}
        data-tracking-id={autoTrackingId}
        {...props}
      >
        {children}
      </button>
    );
  }
);
TabsTrigger.displayName = 'TabsTrigger';

const TabsContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { value: string }
>(({ className, value, ...props }, ref) => {
  const context = React.useContext(TabsContext);
  
  if (!context) {
    throw new Error('TabsContent must be used within a Tabs component');
  }

  const { value: selectedValue } = context;
  
  if (selectedValue !== value) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={cn(
        'mt-2 ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:ring-offset-gray-800 dark:focus-visible:ring-blue-400',
        className
      )}
      {...props}
    />
  );
});
TabsContent.displayName = 'TabsContent';

export { Tabs, TabsList, TabsTrigger, TabsContent };